import { SEARCH_PARAMS_HYDRATED } from './useSearchParamProjection';
import { getSearchMeta } from './searchParamDecorator';
import { toJS, runInAction } from 'mobx';
import type { NavigateFunction } from 'react-router-dom';

/** Options for navigateWithProjection */
export interface NavigateWithProjectionOpts<T extends object> {
  /** The class constructor used when decorating the store fields. */
  ctor: Function;
  /** The target base route (defaults to meta.baseRoute). */
  route?: string;
  /** Values to apply onto the store prior to navigation. */
  patch?: Partial<T>;
  /** Whether to force building the URL manually even if already hydrated (default false). */
  forceUrlBuild?: boolean;
  /** If true, do not mutate store—only compute URL & navigate (default false). */
  computeOnly?: boolean;
  /** Replace history instead of pushing (default true). */
  replace?: boolean;
  /** Optional debug logger */
  debug?: (msg: string, ...args: any[]) => void;
}

/**
 * Unified navigation helper for projected stores.
 *
 * Scenarios:
 *  1. Store already hydrated → mutate store fields (single source of truth). URL projection effect will update search params. Navigate to base route path (without query) to avoid double work.
 *  2. Store not yet hydrated (e.g., navigating very early) → build a URL with serialized values so initial hydration on target page can pick them up.
 *  3. `forceUrlBuild` overrides and always constructs query from the patch (useful when jumping between sibling routes without mounting target store yet).
 */
export function navigateWithProjection<T extends object>(
  store: T,
  navigate: NavigateFunction,
  opts: NavigateWithProjectionOpts<T>
) {
  const { ctor, route, patch, forceUrlBuild = false, computeOnly = false, replace = true, debug } = opts;
  const meta = getSearchMeta(ctor);
  const baseRoute = route || meta.baseRoute || '/';

  const hydrated = Boolean((store as any)[SEARCH_PARAMS_HYDRATED]);
  const mutateStore = hydrated && !forceUrlBuild; // when hydrated we prefer store mutation

  debug?.('[navProj] start', { hydrated, forceUrlBuild, mutateStore, baseRoute, patch });

  let search = '';
  if (!mutateStore) {
    // Need to build query string from provided patch (fall back to current store values if patch missing some fields).
    const params = new URLSearchParams();
    meta.fields.forEach((spec, field) => {
      const nextVal = patch && field in patch ? (patch as any)[field] : (store as any)[field];
      const serialized = spec.serialize(toJS(nextVal));
      if (serialized !== undefined) params.set(spec.key, serialized);
    });
    search = params.toString();
  } else if (patch) {
    // Apply patch atomically so autorun sees a single transaction.
    runInAction(() => {
      for (const [field] of meta.fields) {
        if (patch && Object.prototype.hasOwnProperty.call(patch, field as string)) {
          (store as any)[field] = (patch as any)[field];
        }
      }
    });
  }

  if (computeOnly) {
    return { path: baseRoute, search: search ? `?${search}` : '' };
  }

  const target = search ? `${baseRoute}?${search}` : baseRoute;
  debug?.('[navProj] navigating', { target, replace });
  navigate(target, { replace });
}

